<template>
  <div class="boat-fieldset">
    <b-alert
      v-if="warning"
      show
      :variant="warningVariant"
      class="fieldset-warning"
    >
      {{ warning }}
    </b-alert>

    <b-form-group label="Boat name" label-size="sm">
      <b-form-input v-model="boat.name__c" size="sm" :disabled="disabled" />
    </b-form-group>
    <b-form-group label="Make" label-size="sm">
      <b-form-input v-model="boat.boat_make__c" size="sm" :disabled="disabled" />
      <span v-if="errors.make" class="text-danger small">{{ errors.make }}</span>
    </b-form-group>
    <b-form-group label="Year" label-size="sm">
      <b-form-input v-model="boat.year__c" size="sm" :disabled="disabled" />
      <span v-if="errors.year" class="text-danger small">{{ errors.year }}</span>
    </b-form-group>
    <b-form-group label="Length (ft)" label-size="sm">
      <b-form-input
        v-model="boat.length__c"
        type="number"
        step="1"
        min="1"
        size="sm"
        :disabled="disabled"
      />
      <span v-if="errors.length" class="text-danger small">{{
        errors.length
      }}</span>
    </b-form-group>
    <b-form-group label="Registration / Document #" label-size="sm">
      <b-form-input
        v-model="boat.registration_document__c"
        size="sm"
        :disabled="disabled"
      />
    </b-form-group>
    <b-form-group label="Status" label-size="sm">
      <b-form-select
        v-model="boat.boat_status__c"
        :options="statusOptions"
        size="sm"
        :disabled="disabled"
      />
      <small v-if="primaryStatusHint" class="text-warning d-block">{{
        primaryStatusHint
      }}</small>
    </b-form-group>
  </div>
</template>

<script>
// The boat fields, in one place.
//
// The primary boat and every secondary boat on a membership are edited with
// this same component, so they cannot drift apart in either their markup or
// their rules - which is how the two halves of this form ended up validating
// a year differently in the first place.
//
// `boat` is mutated in place rather than emitted back. The object handed in is
// either the main form's own boat model or a record from the membership
// context, and both callers want the edit to land on the object they passed.
import { boatYearError, boatLengthError, boatMakeError } from "./boatValidation";
import { ACTIVE_BOAT_STATUS, boatStatusOptions } from "@/config/boatStatus";

export default {
  name: "BoatFieldset",
  props: {
    boat: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    // A sync problem on the underlying record, from syncStatus.js. Passed in
    // rather than derived here because the object being edited is not always
    // the row the API returned.
    warning: { type: String, default: null },
    warningVariant: { type: String, default: "warning" },
    // Whether this is the boat the membership points at. Only used to warn:
    // which boat is primary is decided in Salesforce, because that decision
    // can move the area of responsibility and the money with it.
    isPrimaryBoat: { type: Boolean, default: false },
  },
  computed: {
    statusOptions() {
      return boatStatusOptions(this.boat.boat_status__c);
    },
    // Taking the membership's own vessel out of service does not hand the
    // membership a different one - Salesforce still has this boat as the
    // primary, and coverage follows the primary.
    primaryStatusHint() {
      if (!this.isPrimaryBoat) return null;
      const status = this.boat.boat_status__c;
      if (!status || status === ACTIVE_BOAT_STATUS) return null;
      return (
        `This is still the membership's primary boat. Marking it ${status} ` +
        "does not move coverage to another vessel - do that in Salesforce."
      );
    },
    errors() {
      return {
        year: boatYearError(this.boat.year__c),
        length: boatLengthError(this.boat.length__c),
        make: boatMakeError(this.boat.boat_make__c),
      };
    },
    hasErrors() {
      return Object.values(this.errors).some(Boolean);
    },
    // Year, length and make are required, exactly as they are on the main
    // form. The rules are the same rules, from boatValidation.js, so the two
    // places a dealer edits a boat cannot disagree about what a valid one is.
    //
    // 42,345 boats already in Salesforce are missing a year or a length, and
    // 40,120 of those are Active, so opening one of them to fix something else
    // will ask for the missing field first. That is the intent: this is a
    // per-record Save, not the renewal submit, so the cost of insisting is a
    // dealer filling in two boxes rather than a payment that cannot complete.
    blocking() {
      return this.hasErrors;
    },
  },
  watch: {
    // The parent decides whether Save is available, so it needs to know.
    blocking: {
      immediate: true,
      handler(value) {
        this.$emit("validity", !value);
      },
    },
  },
};
</script>

<style scoped>
.fieldset-warning {
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
}
</style>
